import { partition } from "lodash"
import tippy, { Instance, Placement, Props } from "tippy.js"

class TippyTooltip extends HTMLElement {
  // Attributes must be lowercase here.
  static observedAttributes = [
    "interactive",
    "placement",
    "theme",
    "visibility",
    "actionbuttonplacement",
    "topActionsContainerClasses",
    "bottomActionsContainerClasses",
    "zIndex",
  ]

  protected initialized: boolean = false
  protected instance?: Instance

  attributeChangedCallback(name: string): void {
    if (!this.initialized) {
      return
    }
    if (TippyTooltip.observedAttributes.includes(name)) {
      // It's possible that the child element (where the tooltip is attached)
      // will change shortly after an attribute changes. We need to
      // wait for the child so we can attach the tooltip properly. Deferring
      // this to the next event loop iteration appears to address the issue.
      setTimeout(() => this.setTooltip(), 0)
    }
  }

  connectedCallback(): void {
    if (this.initialized) {
      return
    }
    this.initialized = true
    this.setTooltip()
  }

  disconnectedCallback(): void {
    this.destroyTooltip()
    this.initialized = false
  }

  destroyTooltip(): void {
    // Prevent Tippy warnings when destroying an already-destroyed instance
    if (this.instance && !this.instance.state.isDestroyed) {
      this.instance.destroy()
    }
  }

  setTooltip(): void {
    this.destroyTooltip()

    const container = document.createElement("div")

    // If we already had a .tooltip-content element, we want to allow the use
    // of that element as raw HTML and assume whatever component that created
    // it (typically <TippyTooltip>) did the escaping. However, if we only have
    // a "content" attribute, we want to prevent XSS by handling it as text.
    // This alternate attribute path is necessary for non-JSX paths like
    // charting, dialing guidance, and dropdown option tooltips.
    const contentDiv = document.createElement("div")
    // Since querySelector() uses a depth-first pre-order traversal and the
    // children of the tippy instance come before our content element, if there
    // are any nested tippy instances, we'd end up getting the content of the
    // inner most instance instead. Nesting is useful for having a tooltip and
    // popover on the same content. We can't place the content element first
    // because it is hidden and that throws off tooltip positioning. However,
    // we can guarantee that the content and action elements are direct
    // children of this element.
    let sourceContentNode: null | Element = null
    const tooltipActions = []
    for (const child of Array.from(this.children)) {
      if (sourceContentNode === null && child.matches(".tooltip-content")) {
        sourceContentNode = child
      }
      if (child.matches(".tooltip-action")) {
        tooltipActions.push(child)
      }
    }

    if (sourceContentNode && sourceContentNode.innerHTML) {
      contentDiv.innerHTML = sourceContentNode.innerHTML
      const inputs = contentDiv.querySelectorAll("input")
      inputs.forEach(input => {
        input.addEventListener("change", () => {
          ;(sourceContentNode as unknown as { value: { id: string } }).value = {
            id: input.id,
          }
          sourceContentNode!.dispatchEvent(
            new Event("change", { bubbles: true }),
          )
        })
      })
    } else {
      contentDiv.textContent = this.getAttribute("content")
    }

    const [topActions, bottomActions] = partition(
      tooltipActions,
      tooltipAction => tooltipAction.getAttribute("data-placement") === "top",
    )

    const topActionsContainer = document.createElement("div")
    topActions.forEach(tooltipAction => {
      topActionsContainer.appendChild(this.createActionButton(tooltipAction))
    })
    topActionsContainer.setAttribute(
      "class",
      this.getAttribute("topActionsContainerClasses") ?? "",
    )

    const bottomActionsContainer = document.createElement("div")
    bottomActions.forEach(tooltipAction => {
      bottomActionsContainer.appendChild(this.createActionButton(tooltipAction))
    })
    bottomActionsContainer.setAttribute(
      "class",
      this.getAttribute("bottomActionsContainerClasses") ?? "",
    )

    container.appendChild(topActionsContainer)
    container.appendChild(contentDiv)
    container.appendChild(bottomActionsContainer)

    const interactive = this.hasAttribute("interactive")

    const options: Partial<Props> = {
      content: container,
      animation: !interactive,
      interactive,
      appendTo: document.body,
      arrow: true,
      placement:
        (this.getAttribute("placement") as Placement | undefined) ?? "top",
      popperOptions: {
        strategy: "fixed",
        modifiers: [
          // This resolves styling issues when a tooltip's parent has overflow.
          {
            name: "preventOverflow",
            options: {
              boundary: window,
            },
          },
          // The below prevents an issue that causes blurry popup text in
          // Chrome. See https://github.com/popperjs/popper-core/issues/682.
          {
            name: "computeStyles",
            options: {
              gpuAcceleration: false,
            },
          },
        ],
      },
    }

    const zIndex = this.getAttribute("zIndex")
    if (zIndex) {
      options.zIndex = parseInt(zIndex, 10)
    }

    const theme = this.getAttribute("theme")
    if (theme) {
      options.theme = theme
    }

    const visibility = this.getAttribute("visibility")
    if (visibility) {
      options.showOnCreate = visibility === "visible" ? true : false
      options.hideOnClick = false
      options.trigger = "manual"
    }

    this.instance = tippy(this.children[0], options)
  }

  createActionButton(tooltipAction: Element): HTMLElement {
    const actionButton = document.createElement("div")
    actionButton.innerHTML = tooltipAction.innerHTML

    actionButton.addEventListener("click", () => {
      tooltipAction.dispatchEvent(new Event("click", { bubbles: true }))
    })

    return actionButton
  }
}

customElements.define("tippy-tooltip", TippyTooltip)
